import { Alert, Modal, QRCode, Space, Tag, Typography } from 'antd';
import { useMemo } from 'react';
import {
  formatPaymentAmount,
  resolveAnnualBasePriceCents,
  resolveDurationLabel,
  resolveLifetimeMeta,
  resolveMonthlyBasePriceCents,
  resolvePlanPricingMeta,
} from '../utils/checkoutPricing';
import './CheckoutPaymentModal.css';

const CheckoutPaymentModal = ({
  open,
  onCancel,
  checkout,
  allPlans = [],
  agreementHref = '/pay-protocol.html',
  agreementText = '《Publishy付费服务协议》',
  title = '扫码支付',
  qrcodeSize = 180,
  errorMessage = '',
}) => {
  const monthlyBasePriceCents = useMemo(() => resolveMonthlyBasePriceCents(allPlans), [allPlans]);
  const annualBasePriceCents = useMemo(() => resolveAnnualBasePriceCents(allPlans), [allPlans]);
  const isCreditPack = Boolean(checkout?.credit_pack);
  const plan = checkout?.plan || {};
  const creditPack = checkout?.credit_pack || {};
  const product = isCreditPack ? creditPack : plan;
  const productName = isCreditPack
    ? (checkout?.credit_pack?.name || checkout?.product_name || '积分订单')
    : (checkout?.plan?.name || checkout?.plan_name || '订阅订单');
  const checkoutPricing = isCreditPack
    ? {
      hasDiscount: false,
      finalCents: Number(creditPack?.price_cents ?? checkout?.amount_cents ?? 0),
      originalCents: Number(creditPack?.price_cents ?? checkout?.amount_cents ?? 0),
      savedCents: 0,
      discountLabel: '',
    }
    : resolvePlanPricingMeta(plan, monthlyBasePriceCents);
  const checkoutLifetime = isCreditPack
    ? { isLifetime: false, tagLabel: '', message: '' }
    : resolveLifetimeMeta(plan, annualBasePriceCents);
  const checkoutCampaign = checkout?.campaign || null;
  const campaignApplied = !isCreditPack && Boolean(checkoutCampaign?.applied);
  const campaignName = String(checkoutCampaign?.campaign_name || plan?.campaign_label || '').trim();
  const campaignCodeSuffix = String(checkoutCampaign?.code_suffix || plan?.campaign_code_suffix || '').trim();
  const channelCode = String(checkout?.channel_code || '').trim().toLowerCase();
  const displayMode = String(checkout?.display_mode || '').trim().toLowerCase();
  const displayContent = String(
    checkout?.display_content
    || checkout?.code_url
    || '',
  ).trim();
  const wechatPayment = checkout?.payments?.wechat || null;
  const alipayPayment = checkout?.payments?.alipay || null;
  const wechatQrValue = String(
    wechatPayment?.display_content
    || wechatPayment?.code_url
    || '',
  ).trim();
  const alipayQrValue = String(
    alipayPayment?.display_content
    || alipayPayment?.code_url
    || '',
  ).trim();
  const hasDualQr = Boolean(
    checkout?.payments
    && (wechatQrValue || alipayQrValue || String(checkout?.display_mode || '').trim().toLowerCase() === 'multi_qr'),
  );
  const dualAvailableCount = Number(Boolean(wechatQrValue)) + Number(Boolean(alipayQrValue));
  const dualHintText = dualAvailableCount >= 2
    ? '微信/支付宝可任选其一扫码支付'
    : (dualAvailableCount === 1 ? '当前仅有一个可用扫码通道' : '请稍后重试');
  const isAlipayPc = channelCode === 'alipay_pc';
  const isQrMode = Boolean(displayContent) && (displayMode === 'qr_code' || (isAlipayPc && displayMode === 'url'));
  const isUrlMode = displayMode === 'url' && displayContent && !isAlipayPc;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Space direction="vertical" size={16} className="checkout-pay-stack">
        {errorMessage ? (
          <Alert
            type="error"
            showIcon
            message="支付初始化失败"
            description={errorMessage}
            style={{ width: '100%' }}
          />
        ) : null}
        <div className="checkout-pay-summary">
          <Typography.Text strong>{productName}</Typography.Text>
          <div className="checkout-pay-price-card">
            <Typography.Text type="secondary">应付金额</Typography.Text>
            {checkoutPricing.hasDiscount ? (
              <Space size={8}>
                <Typography.Text delete className="checkout-pay-origin">
                  原价 {formatPaymentAmount(checkoutPricing.originalCents, product?.currency)}
                </Typography.Text>
                {checkoutPricing.discountLabel ? <Tag color="volcano">{checkoutPricing.discountLabel}</Tag> : null}
              </Space>
            ) : null}
            <Typography.Title level={3} style={{ margin: 0 }}>
              {formatPaymentAmount(checkoutPricing.finalCents, product?.currency)}
            </Typography.Title>
            {checkoutPricing.hasDiscount ? (
              <Typography.Text className="checkout-pay-saving">
                已优惠 {formatPaymentAmount(checkoutPricing.savedCents, product?.currency)}
              </Typography.Text>
            ) : null}
            {campaignApplied ? (
              <Typography.Text type="secondary">
                已应用渠道码{campaignName ? `：${campaignName}` : ''}
                {campaignCodeSuffix ? `（尾号 ${campaignCodeSuffix}）` : ''}
              </Typography.Text>
            ) : null}
            {isCreditPack ? (
              <Typography.Text type="secondary">
                充值内容：{Number(creditPack?.credits || 0)} 积分
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">
                有效期：{resolveDurationLabel(plan?.duration_days)}
              </Typography.Text>
            )}
            {checkoutLifetime.isLifetime ? (
              <Space direction="vertical" size={0} className="checkout-pay-lifetime">
                <Tag color="gold">{checkoutLifetime.tagLabel}</Tag>
                <Typography.Text>{checkoutLifetime.message}</Typography.Text>
              </Space>
            ) : null}
          </div>
          {hasDualQr ? (
            <Typography.Text type="secondary">
              {dualHintText}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary">
              订单号 {checkout?.out_trade_no || '-'}
            </Typography.Text>
          )}
        </div>

        {hasDualQr ? (
          <div className="checkout-pay-dual-grid">
            <div className="checkout-pay-channel-card">
              <Typography.Text strong>微信支付</Typography.Text>
              {wechatQrValue ? (
                <QRCode value={wechatQrValue} size={qrcodeSize} />
              ) : (
                <Typography.Text type="secondary">暂不可用</Typography.Text>
              )}
            </div>
            <div className="checkout-pay-channel-card">
              <Typography.Text strong>支付宝支付</Typography.Text>
              {alipayQrValue ? (
                <QRCode value={alipayQrValue} size={qrcodeSize} />
              ) : (
                <Typography.Text type="secondary">暂不可用</Typography.Text>
              )}
            </div>
          </div>
        ) : null}

        {!hasDualQr && isQrMode ? (
          <QRCode value={displayContent} size={qrcodeSize} />
        ) : null}

        {!hasDualQr && isUrlMode ? (
          <Space direction="vertical" size={8} style={{ width: '100%', textAlign: 'center' }}>
            <Typography.Text>已创建支付链接，可点击继续支付。</Typography.Text>
            <Typography.Link href={displayContent} target="_blank" rel="noopener noreferrer">
              打开支付页面
            </Typography.Link>
          </Space>
        ) : null}

        {!hasDualQr && !isQrMode && !isUrlMode ? (
          <Typography.Text type="secondary">支付信息已创建，请在支付页面完成支付。</Typography.Text>
        ) : null}

        <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
          支付即视为您已同意
          <a href={agreementHref} target="_blank" rel="noopener noreferrer">{agreementText}</a>
        </Typography.Text>
      </Space>
    </Modal>
  );
};

export default CheckoutPaymentModal;
